import { Context, useContext } from 'react';

// `AuthProviderV1.mjs` (wired in `_app`) writes to the React.Context exported
// from the `.mjs` file. If this `.tsx` calls `createContext` again, it gets a
// totally separate context object that nothing populates — TS consumers then
// always see the empty default and `user` is never set even when logged in.
// Re-use the `.mjs` context so both code paths subscribe to the same one.
import { context as mjsAuthContext } from './AuthContextV1.mjs';

// Loadable state type — matches @porterin/booking-flow's Loadable without cross-package import
type LoadableState<T, P = undefined> = P extends undefined
  ? { state: T }
  : { state: T; data: P };

type Loadable<Success, Failed> =
  | LoadableState<'Init'>
  | LoadableState<'Loading'>
  | LoadableState<'Live', Success>
  | LoadableState<'Error', Failed>;

export interface AuthUser {
  auth_token?: string;
  mobile?: string;
  name?: string;
  requirement_type?: string;
  email?: string;
  whatsapp_consent?: boolean;
}

export interface AgreedDoc {
  doc_type: string;
  doc_url: string;
  created_at: string;
  updated_at: string;
}

export interface AgreedDocs {
  agreed_docs: AgreedDoc[];
  tnc_url: string;
  privacy_policy_url: string;
}

export interface OrderSummary {
  orderCrn: string;
  [key: string]: unknown;
}

export interface AuthContextType {
  is_authenticated: boolean;
  set_is_authenticated: (val: boolean) => void;
  is_authenticated_loadable: Loadable<boolean, string>;
  set_is_authenticated_loadable: (val: Loadable<boolean, string>) => void;
  order_history: Loadable<OrderSummary[], string>;
  set_order_history: (val: Loadable<OrderSummary[], string>) => void;
  user: AuthUser | undefined;
  set_user: (val: AuthUser | undefined) => void;
  agreed_docs: AgreedDocs | undefined;
  set_agreed_docs: (val: AgreedDocs | undefined) => void;
}

// The `.mjs` value's `is_authenticated_loadable` and `order_history` use
// ReScript variants (`0` for Init, etc.) rather than the TS `{ state: 'Init' }`
// shape, hence the cast. TS consumers only read `is_authenticated` and `user`,
// both of which have identical runtime shapes across the two.
const context = mjsAuthContext as Context<AuthContextType>;

export function useAuthContext(): AuthContextType {
  return useContext(context);
}

export { context as AuthContextV1Raw };
